import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatAddress, formatAmount, formatDate } from '../../utils';
import { apiServices } from '../../utils/apiServices';
import { useTheme } from '../../context/ThemeContext'
import { ChevronLeftIcon, ChevronRightIcon, CopyIcon } from '../SVGIcons';
import LinearBorderContainer from '../LinearBorderContainer';

interface Trader {
  id: string;
  wallet_address: string;
  total_trades: number;
  success_rate: number;
  profit: string;
  updated_at: string;
  created_at: string;
  current_stakes_amount: number;
  collateral_amount: number;
}

interface TraderListTableProps {
  mode?: 'detailed' | 'top';
  limit?: number;
}

const TraderListTable: React.FC<TraderListTableProps> = ({ mode = 'detailed', limit = 20 }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof Trader>('total_trades');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const [traders, setTraders] = useState<Trader[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { theme } = useTheme();
  useEffect(() => {
    const fetchTraders = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await apiServices.traders.getAll();
        // Ensure the response matches our Trader interface
        const typedTraders: Trader[] = res.map((trader: any) => ({
          id: trader.id,
          wallet_address: trader.wallet_address,
          total_trades: trader.total_trades,
          success_rate: trader.success_rate,
          profit: trader.profit,
          updated_at: trader.updated_at,
          created_at: trader.created_at,
          current_stakes_amount: trader.current_stakes_amount,
          collateral_amount: trader.collateral_amount
        }));
        setTraders(typedTraders);
      } catch (err) {
        console.error('Failed to fetch traders:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch traders');
        setTraders([]); // Reset traders to empty array on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchTraders();
  }, []);

  const handleCopyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  const handleSort = (field: keyof Trader) => {
    if (mode === 'top') return; // Disable sorting in top mode

    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const filteredAndSortedTraders = useMemo(() => {
    let filtered = traders
      .filter(trader =>
        trader.wallet_address.toLowerCase().includes(searchTerm.toLowerCase())
      );

    if (mode === 'top') {
      // In top mode, sort by profitLoss by default and take top N
      filtered = filtered
        .sort((a, b) => Number(b.profit) - Number(a.profit))
        .slice(0, limit);
    } else {
      // In detailed mode, use the selected sort field and direction
      filtered = filtered.sort((a, b) => {
        const aValue = a[sortField];
        const bValue = b[sortField];
        const direction = sortDirection === 'asc' ? 1 : -1;

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return direction * aValue.localeCompare(bValue);
        }
        return direction * (Number(aValue) - Number(bValue));
      });
    }

    return filtered;
  }, [traders, searchTerm, sortField, sortDirection, mode, limit]);

  const paginatedTraders = mode === 'detailed'
    ? filteredAndSortedTraders.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    )
    : filteredAndSortedTraders;
  const totalPages = Math.ceil(filteredAndSortedTraders.length / itemsPerPage);

  const SortIcon = ({ field }: { field: keyof Trader }) => (
    <span className="ml-1">
      {sortField === field ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
    </span>
  );

  const thClsName = `px-6 py-3 text-left text-xs font-medium text-black/50 dark:text-white/50 tracking-wider font-borna  ${mode === 'detailed' ? 'cursor-pointer hover:bg-black/10 dark:hover:bg-white/10' : ''}`

  return (
    <LinearBorderContainer className="relative transition-colors duration-300">
      {/* <div
        className="absolute inset-0 rounded-[24px] pointer-events-none border border-white/20 dark:border-white/20"
        style={{
          zIndex: 10,
          mask: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 50%, rgba(0,0,0,0) 100%)',
          WebkitMask: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 50%, rgba(0,0,0,0) 100%)'
        }}
      /> */}
      <div className="">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center px-4 sm:px-6 py-4 space-y-4 sm:space-y-0">
          <h2 className="text-xl text-gray-800 dark:text-gray-200 font-borna">
            {mode === 'top' ? 'Top Traders' : 'Traders'}
          </h2>
          {error && (
            <div className="text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}
          {isLoading && (
            <div className="text-gray-600 dark:text-gray-400 text-sm">
              Loading...
            </div>
          )}
          {mode === 'detailed' && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
              <div className="relative w-full sm:w-auto">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black dark:text-white pointer-events-none">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M4 9.5C4 6.46243 6.46243 4 9.5 4C12.5376 4 15 6.46243 15 9.5C15 11.0191 14.3855 12.3927 13.3891 13.3891C12.3927 14.3855 11.0191 15 9.5 15C6.46243 15 4 12.5376 4 9.5ZM9.5 2C5.35786 2 2 5.35786 2 9.5C2 13.6421 5.35786 17 9.5 17C11.2101 17 12.7876 16.4267 14.0491 15.4633L15 16.4142V18L19 22L21 21L22 19L18 15H16.4142L15.4633 14.0491C16.4267 12.7876 17 11.2101 17 9.5C17 5.35786 13.6421 2 9.5 2Z" fill="currentColor" fillOpacity="0.3" />
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder="Search traders"
                  className="input-field bg-[#F2F2F2] dark:bg-[#262626] rounded-[100px] h-[36px] pl-10 pr-4 py-0 w-full sm:w-auto"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="relative w-full sm:w-auto">
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className={`input-field text-black dark:text-white bg-[#F2F2F2] dark:bg-[#262626] rounded-[100px] h-[36px] pr-10 pl-4 py-0 appearance-none w-full sm:w-auto ${theme === 'dark' ? 'dark' : ''}`}
                >
                  <option value="10">10 per page</option>
                  <option value="25">25 per page</option>
                  <option value="50">50 per page</option>
                  <option value="100">100 per page</option>
                </select>
                <span className="pointer-events-none absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path d="M19 9l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="">
        {/* Mobile Card View */}
        <div className="block md:hidden px-4 sm:px-6 pb-4">
          {paginatedTraders.map((trader) => (
            <div
              key={trader.id}
              onClick={() => navigate(`/trader/${trader.wallet_address}`)}
              className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-3 border border-black/6 dark:border-white/20 cursor-pointer hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-200 truncate">
                      {formatAddress(trader.wallet_address)}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyAddress(trader.wallet_address);
                      }}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-md transition-colors"
                      title="Copy address"
                    >
                      {copiedAddress === trader.wallet_address ? (
                        <span className="text-green-600 block h-[17px] w-[16px]">✓</span>
                      ) : (
                        <CopyIcon fillColor={theme === 'dark' ? 'white' : 'black'} />
                      )}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Trades:</span>
                      <span className="ml-1 text-gray-900 dark:text-gray-200">{trader.total_trades}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Success:</span>
                      <span className="ml-1 text-gray-900 dark:text-gray-200">{trader.success_rate.toFixed(3)}%</span>
                    </div>
                    {mode === 'detailed' && (
                      <>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Collateral:</span>
                          <span className="ml-1 text-gray-900 dark:text-gray-200">{formatAmount("" + trader.collateral_amount)}τ</span>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Last Trade:</span>
                          <span className="ml-1 text-gray-900 dark:text-gray-200">{formatDate(trader?.updated_at || trader?.created_at)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-medium ${Number(trader.profit) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {formatAmount(trader?.profit)}τ
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">P/L</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[rgba(0,0,0,0.06)] dark:divide-white/20">
              <thead className="">
                <tr >
                  <th
                    className={thClsName}
                    onClick={() => handleSort('wallet_address')}
                  >
                    Address {mode === 'detailed' && <SortIcon field="wallet_address" />}
                  </th>
                  <th
                    className={thClsName}
                    onClick={() => handleSort('total_trades')}
                  >
                    Total Trades {mode === 'detailed' && <SortIcon field="total_trades" />}
                  </th>
                  <th
                    className={thClsName}
                    onClick={() => handleSort('success_rate')}
                  >
                    Success Rate {mode === 'detailed' && <SortIcon field="success_rate" />}
                  </th>
                  <th
                    className={thClsName}
                    onClick={() => handleSort('profit')}
                  >
                    P/L {mode === 'detailed' && <SortIcon field="profit" />}
                  </th>
                  {mode === 'detailed' && (
                    <>
                      <th
                        className={thClsName}
                        onClick={() => handleSort('collateral_amount')}
                      >
                        Collateral {mode === 'detailed' && <SortIcon field="collateral_amount" />}
                      </th>
                      <th
                        className={thClsName}
                        onClick={() => handleSort('updated_at')}
                      >
                        Last Trade <SortIcon field="updated_at" />
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="">
                {paginatedTraders.map((trader) => (
                  <tr
                    key={trader.id}
                    onClick={() => navigate(`/trader/${trader.wallet_address}`)}
                    className="cursor-pointer hover:bg-[#F2F2F2] dark:hover:bg-[#262626]"
                  >
                    <td className="px-6 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-200">
                      <div className="flex items-center space-x-2">
                        <span
                          className="group relative"
                          title={trader.wallet_address}
                        >
                          {formatAddress(trader.wallet_address)}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyAddress(trader.wallet_address);
                          }}
                          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-md transition-colors"
                          title="Copy address"
                        >
                          {copiedAddress === trader.wallet_address ? (
                            <span className="text-green-600 block h-[17px] w-[16px]">✓</span>
                          ) : (
                            <CopyIcon fillColor={theme === 'dark' ? 'white' : 'black'} />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{trader.total_trades}</td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{trader.success_rate.toFixed(3)}%</td>
                    <td className={`px-6 py-2 whitespace-nowrap text-sm font-medium ${Number(trader.profit) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      }`}>{formatAmount(trader?.profit)}τ</td>
                    {mode === 'detailed' && (
                      <>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{formatAmount("" + trader.collateral_amount)}τ</td>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{formatDate(trader?.updated_at || trader?.created_at)}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {mode === 'detailed' && (
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-4 px-4 sm:px-6 space-y-3 sm:space-y-0">
            <div className="text-sm text-black/50 dark:text-white/50">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedTraders.length)} of {filteredAndSortedTraders.length} traders
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-1 py-1 border text-black/70 dark:text-white/70 border-black/50 dark:border-white/50 rounded-[100%] disabled:opacity-50"
              >
                <ChevronLeftIcon fillColor={currentPage === 1 ? 'gray' : 'white'} />
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-1 py-1 border text-black/70 dark:text-white/70 border-black/50 dark:border-white/50 rounded-[100%] disabled:opacity-50"
              >
                <ChevronRightIcon fillColor={currentPage === totalPages ? 'gray' : 'white'} />
              </button>
            </div>
          </div>
        )}
      </div>
    </LinearBorderContainer>
  );
};

export default TraderListTable; 